import {
  init,
  classModule,
  propsModule,
  styleModule,
  eventListenersModule,
  h,
  VNode,
} from "snabbdom";

import { GameState, GameCreateResponse, Route } from "./types";
import { Network } from "./network";
import { view } from "./render";

const patch = init([
  classModule,
  propsModule,
  styleModule,
  eventListenersModule,
]);

const backendUri: string = findBackendUrl();
function findBackendUrl(): string {
  if (window.location.port === "1234") {
    // local, backend at localhost:
    return "http://" + window.location.hostname + ":10067/api/v1";
  } else {
    // assume deployed somewhere and backend is available at same host/port as frontend
    return window.location.protocol + "//" + window.location.host + "/api/v1";
  }
}

const frontendUri: string = findFrontendUrl();
function findFrontendUrl(): string {
  if (window.location.port === "1234") {
    // local, frontend at port 1234 at localhost:
    return "http://" + window.location.hostname + ":1234";
  } else {
    return window.location.protocol + "//" + window.location.host;
  }
}

const Routes: Route[] = [
  {
    path: "/",
    view: () =>
      h("div", [
        h("h1", "Home"),
        h("p", "Welcome to stress!"),
        h(
          "button",
          { on: { click: () => navigate("/game/create") } },
          "Create Game",
        ),
      ]),
  },
  {
    path: "/game/create",
    view: () => {
      gameData = null;
      isLoading = false;
      return initGame();
    },
  },
  {
    path: "/game/:id/join",
    view: (params) => joinGame(params["id"]), // 'id' comes from the URL
  },
];

let render_interval: number;
let vnode: VNode = document.getElementById("app") as unknown as VNode;
const network: Network = new Network();

function navigate(path: string): void {
  window.history.pushState({}, "", path);
  router(); // Manually trigger the route change
}

// A simple helper to turn "/game/:id/join" into a Regex
function matchPath(
  routePath: string,
  currentPath: string,
): Record<string, string> | null {
  const paramNames: string[] = [];
  const regexPath =
    routePath.replace(/:([^\/]+)/g, (_, prop) => {
      paramNames.push(prop);
      return "([^\/]+)";
    }) + "$";

  const match = currentPath.match(new RegExp(regexPath));
  if (!match) return null;

  // Extract params: { id: "123" }
  return match.slice(1).reduce(
    (params, value, index) => {
      params[paramNames[index]] = value;
      return params;
    },
    {} as Record<string, string>,
  );
}

function router(): void {
  const currentPath = window.location.pathname;

  // Find the first matching route
  let matchedRoute = null;
  let params = {};

  for (const route of Routes) {
    const match = matchPath(route.path, currentPath);
    if (match) {
      matchedRoute = route;
      params = match;
      break;
    }
  }

  // Fallback to 404 if no match
  const viewContent: VNode = matchedRoute
    ? matchedRoute.view(params)
    : h("div", [
        h("h1", "Not Found"),
        h("p", "This site does not seem to exist does it?"),
      ]);

  vnode = patch(vnode, viewContent);
}

let gameData: GameCreateResponse | null = null;
let isLoading = false;
function initGame(): VNode {
  if (!gameData && !isLoading) {
    isLoading = true;
    fetch(backendUri + "/game/create", { method: "POST" })
      .then((res) => res.json())
      .then((data: GameCreateResponse) => {
        gameData = data;
        isLoading = false;
        vnode = patch(vnode, initGame());
      });
  }

  if (isLoading || !gameData) {
    return h("div", [h("p", "Creating game...")]);
  }

  return h("div", [
    h("p", "Created a game!"),
    h(
      "button",
      {
        on: {
          click: () =>
            navigator.clipboard.writeText(
              `${frontendUri}/game/${gameData!.id}/join`, // TODO: correct link
            ),
        },
      },
      "Copy link",
    ),
    h(
      "button",
      {
        on: { click: () => navigate(`/game/${gameData!.id}/join`) },
      },
      "Join game",
    ),
  ]);
}

function joinGame(id: string): VNode {
  network.connect(backendUri + `/game/${id}/join`);
  render();
  render_interval = window.requestAnimationFrame(render);
  return vnode;
}

export function printState(state: GameState): string {
  return (
    "lPile: " +
    state.leftPile.length +
    " rPile: " +
    state.leftPile.length +
    " sidePile: " +
    state.sidePile.length +
    " total cards: " +
    Number(
      state.sidePile.length +
        state.leftPile.length +
        state.rightPile.length +
        state.playerZero.cardCount() +
        state.playerOne.cardCount(),
    )
  );
}

function render(): void {
  const newVnode = view(network.renderState());
  vnode = patch(vnode, newVnode);
  window.requestAnimationFrame(render);
}

// Listen for back/forward navigation
window.addEventListener("popstate", router);

// Initial render
router();
